// @ts-nocheck
import React, { useMemo, useRef, useState } from 'react';
import { fetchDataPointsReport, downloadDataPointsTranscriptPdf } from '../services/api';
import { GRADING_STYLES, colorFromScore } from '../utils/grading';

export default function StudentTranscript({ students }: any) {
  const [studentId, setStudentId] = useState<any>('');
  const [startDate, setStartDate] = useState<any>('');
  const [endDate, setEndDate] = useState<any>('');
  const [rows, setRows] = useState<any>(null);
  const [busy, setBusy] = useState<any>(false);
  const [downloading, setDownloading] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const request = useRef<any>(0);
  const studentSelect = useRef<any>(null);
  const student = students.find((item) => String(item.student_id) === studentId);
  const studentName = student
    ? `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.full_name || `Student ${studentId}`
    : '';
  const invalidRange = Boolean(startDate && endDate && startDate > endDate);
  const period = `${startDate || 'Beginning of available history'} to ${endDate || 'Present'}`;

  const summary = useMemo(() => {
    const standards = new Map();
    (rows || []).forEach((row) => {
      const key = JSON.stringify([row.subject_name, row.standard_code]);
      const existing = standards.get(key);
      if (!existing) standards.set(key, { ...row, count: 1 });
      else {
        const later = String(row.date || '') > String(existing.date || '')
          || (row.date === existing.date && row.id > existing.id);
        standards.set(key, { ...(later ? row : existing), count: existing.count + 1 });
      }
    });
    return [...standards.values()].sort((a, b) =>
      String(a.subject_name || '').localeCompare(String(b.subject_name || ''))
      || String(a.standard_code || '').localeCompare(String(b.standard_code || ''), undefined, { numeric: true }));
  }, [rows]);

  const changeSelection = (setter, value) => {
    request.current += 1;
    setter(value);
    setRows(null);
    setError('');
    setBusy(false);
  };

  const generate = async (event) => {
    event.preventDefault();
    if (!student || invalidRange || busy) return;
    const current = ++request.current;
    setBusy(true);
    setRows(null);
    setError('');
    try {
      const data = await fetchDataPointsReport({ studentId });
      const points = data.filter((row) => String(row.student_id) === studentId
        && (!startDate || (row.date && String(row.date).slice(0, 10) >= startDate))
        && (!endDate || (row.date && String(row.date).slice(0, 10) <= endDate)));
      if (current === request.current) setRows(points);
    } catch (err: any) {
      if (current === request.current) setError(err.message || 'Could not generate the transcript. Please try again.');
    } finally {
      if (current === request.current) setBusy(false);
    }
  };

  const download = async () => {
    if (!rows?.length || downloading) return;
    setDownloading(true);
    setError('');
    try {
      const blob = await downloadDataPointsTranscriptPdf({
        ids: rows.map((row) => row.id),
        filters: `Student: ${studentName} · ${period} · All accessible subjects`,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-${studentId}-transcript.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      setError(err.message || 'Could not download the transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const controlStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--ae-border)', font: 'inherit', maxWidth: '100%' };
  const buttonStyle = { ...controlStyle, background: 'var(--ae-primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' };
  const cellStyle = { padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--ae-border)' };

  return (
    <section aria-labelledby="student-transcript-heading" style={{ padding: 20, marginBottom: 28, border: '1px solid var(--ae-border)', borderRadius: 14, background: 'var(--ae-surface)' }}>
      <h2 id="student-transcript-heading" style={{ marginTop: 0, color: 'var(--ae-primary-strong)' }}>Student Transcript</h2>
      <p>Generate a standards-based transcript across all subjects you can access. Leave dates blank for all available history.
        The preview shows the latest recorded status per standard; the PDF includes the underlying assessment history.</p>
      {rows === null && (
      <form onSubmit={generate} style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'end' }}>
        <label>Student<br />
          <select ref={studentSelect} aria-label="Transcript student" value={studentId} onChange={(event) => changeSelection(setStudentId, event.target.value)} style={controlStyle} disabled={downloading} required>
            <option value="">Select a student</option>
            {students.map((item) => (
              <option key={item.student_id} value={item.student_id}>
                {`${item.first_name || ''} ${item.last_name || ''}`.trim() || item.full_name || `Student ${item.student_id}`}
              </option>
            ))}
          </select>
        </label>
        <label>From<br /><input type="date" aria-label="Transcript start date" value={startDate} onChange={(event) => changeSelection(setStartDate, event.target.value)} style={controlStyle} disabled={downloading} /></label>
        <label>Through<br /><input type="date" aria-label="Transcript end date" value={endDate} onChange={(event) => changeSelection(setEndDate, event.target.value)} style={controlStyle} disabled={downloading} /></label>
        <button type="submit" style={buttonStyle} disabled={!student || invalidRange || busy || downloading}>
          {busy ? 'Generating transcript…' : 'Generate Transcript'}
        </button>
      </form>
      )}
      {invalidRange && <p role="alert">The start date must be on or before the end date.</p>}
      {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
      {rows && (
        <div aria-label="Generated student transcript" role="region" style={{ marginTop: 20 }}>
          <button
            type="button"
            disabled={downloading}
            style={controlStyle}
            onClick={() => {
              setRows(null);
              setError('');
              request.current += 1;
              setTimeout(() => studentSelect.current?.focus(), 0);
            }}
          >
            Back to student selection
          </button>
          <h3>{studentName}</h3>
          <p>{period} · {rows.length} data points · {summary.length} standards</p>
          {rows.length === 0 ? <p>No data points are available for this student in the selected period.</p> : (
            <>
              <button type="button" onClick={download} disabled={downloading} style={buttonStyle}>
                {downloading ? 'Downloading transcript…' : 'Download Transcript PDF'}
              </button>
              <div style={{ overflowX: 'auto', marginTop: 16 }}>
                <table aria-label="Student transcript standards" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Subject', 'Standard', 'Description', 'Latest status', 'Latest score', 'Date', 'Data points'].map((title) => <th key={title} scope="col" style={cellStyle}>{title}</th>)}</tr></thead>
                  <tbody>{summary.map((row) => (
                    <tr key={JSON.stringify([row.subject_name, row.standard_code])}>
                      <td style={cellStyle}>{row.subject_name || 'Other'}</td>
                      <td style={cellStyle}>{row.standard_code || 'No standard'}</td>
                      <td style={cellStyle}>{row.standard_description || ''}</td>
                      <td style={cellStyle}>{GRADING_STYLES[colorFromScore(row.score) || row.color]?.label || 'Not assessed'}</td>
                      <td style={cellStyle}>{row.score ?? '—'}</td>
                      <td style={cellStyle}>{row.date || '—'}</td>
                      <td style={cellStyle}>{row.count}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
